import React, { useContext, useState } from 'react';
import PropTypes from 'prop-types';
import { get, map, filter } from 'lodash';
import { useNavigate } from 'react-router-dom';
import { I18nReactContext } from '../../../contexts/I18nContext';
import useSignDocTypes from '../../../Hooks/useSignDocTypes';
import { Button } from '../../Form/Button/Button';
import { SIGNDOC_STATUS, SIGNDOC_GROUP_TYPES } from '../SignDocLib';
import { RecurringAtEditModal } from '../RecurringAtEditModal/RecurringAtEditModal';
import { isLearningPathUnordered } from '../../../Utils/SignDocs/SignDocs';
import Tooltip from '@mui/material/Tooltip';
import './SignDocVersionCard.scss';

// MUI Icons TODO: change the view from regular text buttons to icons
import EditIcon from '@mui/icons-material/Edit';
import BarChartIcon from '@mui/icons-material/BarChart';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import AssessmentIcon from '@mui/icons-material/Assessment';
import AutorenewIcon from '@mui/icons-material/Autorenew';

/**
 * SignDocVersionCard - Wide card component for displaying SignDoc version information with conditional views
 *
 * Features:
 * - DEFAULT VIEW: Image, name, metadata (left) | Organizations (middle) | Edit/Statistics buttons (right)
 * - STATISTICS VIEW: Image, name, metadata (left) | Statistics in horizontal row (middle) | Return/Reports buttons (right)
 * - Smooth transition when toggling between views
 *
 * @param {object} props - Component props
 * @param {object} props.version - Version data object
 * @param {string} props.version.id - Version ID
 * @param {number} props.version.groupVersion - Version number (0-based)
 * @param {string} props.version.docName - Version name/title
 * @param {string} props.version.description - Version description
 * @param {string} props.version.status - Version status ('active', 'archived', or 'scheduled')
 * @param {string} props.version.createdAt - ISO date string of creation
 * @param {string} [props.version.type] - SignDoc type (e.g., 'EXAM', 'SURVEY')
 * @param {string} [props.version.imgSrc] - SignDoc image URL
 * @param {string} [props.version.expiredAt] - ISO date string of expiration (future feature)
 * @param {Array<object>} [props.version.organizationSignDoc] - Array of organization assignments
 * @param {number} [props.version.assignedCount] - Total users assigned to this version
 * @param {number} [props.version.completedCount] - Total users who completed this version
 * @param {number} [props.version.inProgressCount] - Total users in progress
 * @param {function} props.onEdit - Callback when edit button clicked
 * @param {function} props.onViewStatistics - Callback when statistics button clicked (optional, not used internally)
 * @param {boolean} [props.isCurrentVersion=false] - Whether this is the current active version
 * @returns {JSX.Element} Rendered version card component
 */
export const SignDocVersionCard = ({
    version,
    onEdit,
    onViewStatistics,
    onRecurringAtUpdated,
    isCurrentVersion = false
}) => {
    const { i18n } = useContext(I18nReactContext);
    const { getMgmntImageIcon, getIsPreviewable, getPreviewMode } = useSignDocTypes();
    const navigate = useNavigate();
    const [showStatistics, setShowStatistics] = useState(false);
    const [isRecurringAtEditOpen, setIsRecurringAtEditOpen] = useState(false);

    // Extract version data with safe defaults
    const signDocId = get(version, 'id', '');
    const signDocName = get(version, 'docName', '');
    const signDocType = get(version, 'type', '');
    const imgSrc = get(version, 'imgSrc', '');
    const createdAt = get(version, 'createdAt', '');
    const expiredAt = get(version, 'expiredAt', null);
    const groupVersion = get(version, 'groupVersion', 0);
    const recurringDuration = get(version, 'recurringDuration', null);
    const nextVersionDueDate = get(version, 'nextVersionDueDate', null);
    const groupType = get(version, 'groupType', null);
    const recurringAt = get(version, 'recurringAt', null);

    const isPreviewable = getIsPreviewable({ type: signDocType });
    const previewMode = getPreviewMode({ type: signDocType });
    const linkUrl = get(version, 'linkUrl', '');
    const courseLink = get(version, 'courseLink', '');

    const hasPreviewUrl = previewMode === 'externalLink' ? Boolean(linkUrl || courseLink) : true;
    const canPreview = isPreviewable && hasPreviewUrl;

    // Show pencil icon only for ACTIVE SEQUENTIAL SignDocs with a nextVersionDueDate
    const canEditRecurringAt = version.status === SIGNDOC_STATUS.ACTIVE
        && groupType === SIGNDOC_GROUP_TYPES.SEQUENTIAL
        && nextVersionDueDate;

    // Statistics data
    const assignedCount = get(version, 'assignedCount', 0);
    const completedCount = get(version, 'completedCount', 0);
    const inProgressCount = get(version, 'inProgressCount', 0);
    // Use backend-calculated percentage (with 1 decimal place)
    const progressPercentage = get(version, 'progressPercentage', 0);

    let versionOrganizations = get(version, 'organizationSignDoc', []);
    versionOrganizations = filter(versionOrganizations, org => !get(org, 'organization.isPersonal', false));

    /**
     * Format date for display
     * @param {string} dateString - ISO date string
     * @returns {string} Formatted date string
     */
    const formatDate = (dateString) => {
        if (!dateString) return '';
        const date = new Date(dateString);
        return date.toLocaleDateString();
    };

    /**
     * Handle viewing statistics - flips card to show statistics view
     */
    const handleViewStatistics = () => {
        setShowStatistics(true);
    };

    /**
     * Handle returning to data view - flips card back to front
     */
    const handleReturnToData = () => {
        setShowStatistics(false);
    };

    /**
     * Handle navigating to full reports page
     */
    const handleGoToReports = () => {
        navigate(`/reports/course-results/${signDocId}?organizationIds=all`);
    };

    /**
     * Handle previewing SignDoc content in a new tab
     */
    const handlePreview = () => {
        if (previewMode === 'externalLink') {
            const url = linkUrl || courseLink;
            if (url) {
                window.open(url, '_blank', 'noopener,noreferrer');
            }
        } else if (previewMode === 'signedUrl') {
            window.open(`/admin/signDoc/${signDocId}/preview`, '_blank', 'noopener,noreferrer');
        }
    };

    const versionNumber = groupVersion + 1;

    /**
     * Renders the left column content (image, name, badges, metadata)
     * This is the SAME for both front and back faces
     */
    const renderLeftColumn = () => (
        <div className="leftColumn">
            {/* Row 1: Image and Text side by side */}
            <div className="imageAndTextRow">
                <div className="signDocImageWrapper">
                    <img
                        src={imgSrc || getMgmntImageIcon({ type: signDocType })}
                        alt={signDocName}
                        className="signDocImage"
                    />
                </div>

                <div className="signDocTextInfo">
                    <div className="signDocName" title={signDocName}>
                        {signDocName}
                    </div>
                    <div className="badgesRow">
                        <span className="versionBadge">
                            V{versionNumber}
                        </span>
                        {signDocType && (
                            <span className="typeBadge" data-testid={`version-type-${signDocId}`}>
                                {i18n.translate(`signDocTypes.${signDocType}`)}
                            </span>
                        )}
                        <span
                            className={`statusBadge statusBadge--${version.status}`}
                            data-testid={`version-status-${signDocId}`}
                        >
                            {i18n.translate(`signDocVersionManagement.status.${version.status}`)}
                        </span>
                        {recurringDuration && (
                            <span
                                className={`recurringBadge ${version.status === SIGNDOC_STATUS.ARCHIVED ? 'recurringBadge--archived' : ''}`}
                                data-testid={`version-recurring-${signDocId}`}
                            >
                                <AutorenewIcon className="recurringIcon" />
                                {i18n.translate('signDocVersionManagement.recurring.badge')}
                            </span>
                        )}
                        {isLearningPathUnordered(version) && (
                            <span
                                className={`freePathBadge ${version.status === SIGNDOC_STATUS.ARCHIVED ? 'freePathBadge--archived' : ''}`}
                                data-testid={`version-free-path-${signDocId}`}
                            >
                                {i18n.translate('learningPath.freePath.badge')}
                            </span>
                        )}
                    </div>
                </div>
            </div>

            {/* Row 2: Metadata */}
            <div className="metadataSection">
                <div className="metadataItem">
                    <div className="metadataLabel">
                        {i18n.translate('signDocVersionManagement.created')}
                    </div>
                    <div className="metadataValue" data-testid={`version-date-${signDocId}`}>
                        {formatDate(createdAt)}
                    </div>
                </div>

                {expiredAt && (
                    <div className="metadataItem">
                        <div className="metadataLabel">
                            {i18n.translate('signDocVersionManagement.expiredAt')}
                        </div>
                        <div className="metadataValue" data-testid={`version-expired-${signDocId}`}>
                            {formatDate(expiredAt)}
                        </div>
                    </div>
                )}

                {/* Only show next version date for non-archived SignDocs */}
                {nextVersionDueDate && version.status !== SIGNDOC_STATUS.ARCHIVED && (
                    <div className="metadataItem metadataItem--spaced">
                        <div className="metadataLabel">
                            {i18n.translate('signDocVersionManagement.recurring.nextVersionDate')}
                        </div>
                        <div className="metadataValue metadataValue--with-action" data-testid={`version-next-date-${signDocId}`}>
                            {formatDate(nextVersionDueDate)}
                            {canEditRecurringAt && (
                                <Tooltip title={i18n.translate('signDocVersionManagement.recurring.editNextVersionDate')}>
                                    <span
                                        className="metadataItem__change-link"
                                        onClick={() => setIsRecurringAtEditOpen(true)}
                                        data-testid={`edit-recurring-at-${signDocId}`}
                                    >
                                        <EditIcon className="metadataItem__change-link-icon" />
                                        {i18n.translate('signDocVersionManagement.recurring.changeDate')}
                                    </span>
                                </Tooltip>
                            )}
                        </div>
                    </div>
                )}
            </div>
        </div>
    );

    return (
        <div
            className={`SignDocVersionCard ${isCurrentVersion ? 'SignDocVersionCard--current' : ''}`}
            data-testid={`version-card-${signDocId}`}
        >
            <div className="cardInner">
                <div className="signDocInfoSection">
                    {/* LEFT COLUMN: Image + Name/Badges + Metadata (ALWAYS VISIBLE) */}
                    {renderLeftColumn()}

                    {/* MIDDLE COLUMN: Conditional - Organizations OR Statistics */}
                    <div className="middleColumn">
                        {!showStatistics ? (
                            // Show Organizations when NOT showing statistics (exclude personal organizations)
                            versionOrganizations.length > 0 && (
                                <div className="organizationsWrapper">
                                    {versionOrganizations.slice(0, 10).map((org, index) => (
                                        <span key={index} className="organizationName">
                                            {get(org, 'organization.orgName', '')}
                                        </span>
                                    ))}
                                    {versionOrganizations.length > 10 && (
                                        <span className="organizationName">...</span>
                                    )}
                                </div>
                            )
                        ) : (
                            // Show Statistics in HORIZONTAL ROW when showing statistics
                            <div className="statisticsWrapper">
                                {/* Statistic 1: Progress Done */}
                                <div className="statisticItem">
                                    <div className="statisticLabel">
                                        {i18n.translate('signDocVersionManagement.statistics.progressDone')}
                                    </div>
                                    <div className="statisticDivider"></div>
                                    <div className="statisticValue statisticValue--large">
                                        {progressPercentage.toFixed(1)}%
                                    </div>
                                </div>

                                {/* Statistic 2: Users Assigned */}
                                <div className="statisticItem">
                                    <div className="statisticLabel">
                                        {i18n.translate('signDocVersionManagement.statistics.usersAssigned')}
                                    </div>
                                    <div className="statisticDivider"></div>
                                    <div className="statisticValue">
                                        {assignedCount}
                                    </div>
                                </div>

                                {/* Statistic 3: Users Completed */}
                                <div className="statisticItem">
                                    <div className="statisticLabel">
                                        {i18n.translate('signDocVersionManagement.statistics.usersCompleted')}
                                    </div>
                                    <div className="statisticDivider"></div>
                                    <div className="statisticValue">
                                        {completedCount}
                                    </div>
                                </div>

                                     {/* Statistic 4: Users In Progress */}
                                     <div className="statisticItem">
                                    <div className="statisticLabel">
                                        {i18n.translate('signDocVersionManagement.statistics.usersInProgress')}
                                    </div>
                                    <div className="statisticDivider"></div>
                                    <div className="statisticValue">
                                        {inProgressCount}
                                    </div>
                                </div>
                            </div>
                        )}
                    </div>

                    {/* RIGHT COLUMN: Conditional - Edit/Statistics buttons OR Return/Reports buttons */}
                    <div className="rightColumn">
                        <div className="actionButtonsWrapper">
                            {!showStatistics ? (
                                // Show Edit and View Statistics buttons
                                <>
                                    {canPreview && (
                                        <Button
                                            color="secondary"
                                            onClick={handlePreview}
                                            text={String(i18n.translate('signDocVersionManagement.preview'))}
                                            data-testid={`preview-version-${signDocId}`}
                                        />
                                    )}
                                    <Button
                                        color="secondary"
                                        onClick={() => onEdit(version)}
                                        text={String(i18n.translate('signDocVersionManagement.edit'))}
                                        data-testid={`edit-version-${signDocId}`}
                                    />
                                    <Button
                                        color="primary"
                                        onClick={handleViewStatistics}
                                        text={String(i18n.translate('signDocVersionManagement.viewStatistics'))}
                                        data-testid={`view-statistics-${signDocId}`}
                                    />
                                </>
                            ) : (
                                // Show Return and Go to Reports buttons
                                <>
                                    <Button
                                        color="primary"
                                        onClick={handleGoToReports}
                                        text={String(i18n.translate('signDocVersionManagement.goToReports'))}
                                        data-testid={`go-to-reports-${signDocId}`}
                                    />
                                    <Button
                                        color="secondary"
                                        onClick={handleReturnToData}
                                        text={String(i18n.translate('signDocVersionManagement.returnToData'))}
                                        data-testid={`return-to-data-${signDocId}`}
                                    />
                                </>
                            )}
                        </div>
                    </div>
                </div>
            </div>

            {/* RecurringAt Edit Modal */}
            {canEditRecurringAt && (
                <RecurringAtEditModal
                    signDocId={signDocId}
                    currentRecurringAt={recurringAt || nextVersionDueDate}
                    isOpen={isRecurringAtEditOpen}
                    onClose={() => setIsRecurringAtEditOpen(false)}
                    onSuccess={onRecurringAtUpdated}
                />
            )}
        </div>
    );
};

SignDocVersionCard.propTypes = {
    version: PropTypes.shape({
        id: PropTypes.string.isRequired,
        groupVersion: PropTypes.number.isRequired,
        docName: PropTypes.string.isRequired,
        description: PropTypes.string,
        status: PropTypes.oneOf([SIGNDOC_STATUS.ACTIVE, SIGNDOC_STATUS.ARCHIVED, SIGNDOC_STATUS.SCHEDULED]).isRequired,
        createdAt: PropTypes.string.isRequired,
        type: PropTypes.string,
        imgSrc: PropTypes.string,
        expiredAt: PropTypes.string,
        organizationSignDoc: PropTypes.arrayOf(PropTypes.object),
        assignedCount: PropTypes.number,
        completedCount: PropTypes.number,
        inProgressCount: PropTypes.number,
        linkUrl: PropTypes.string,
        courseLink: PropTypes.string
    }).isRequired,
    onEdit: PropTypes.func.isRequired,
    onViewStatistics: PropTypes.func,
    onRecurringAtUpdated: PropTypes.func,
    isCurrentVersion: PropTypes.bool
};
